// Translation provider component and context
// Hook has been moved to separate file for better Fast Refresh compatibility

import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';

declare global {
  interface Window {
    debugTranslations?: () => void;
  }
}

import { DEFAULT_LANG } from '@/constants';
import { languageToUrlCode, normalizeLanguageCode } from '@/utils/language';

import {
  loadLanguageTranslations,
  preloadNamespaces,
  loadLanguageNamespace as _loadLanguageNamespace,
  cleanupUnusedLanguages,
} from './translation/translationRegistry';

const FALLBACK_LANGUAGE = 'en';

// Type definitions for translation context
interface TranslationContextValue {
  t: (
    page: string,
    context: string,
    fallback?: string | null,
    variables?: Record<string, unknown>
  ) => string;
  currentLanguage: string;
  changeLanguage: (languageCode: string) => Promise<void>;
  loading: boolean;
  forceUpdate: number;
  translations: Record<string, unknown>;
  fallbackTranslations: Record<string, unknown>;
  debugTranslations: () => void;
}

interface TranslationProviderProps {
  children: React.ReactNode;
  initialLanguage?: string;
}

// Preload critical languages for better performance
// These are the most commonly used languages based on traffic
const CRITICAL_LANGUAGES = ['en', 'ar', 'ko'];

// Core namespaces that are always loaded for basic functionality
const CORE_NAMESPACES = ['common', 'navigation', 'buttons'];

// Additional namespaces that can be lazy loaded
const _LAZY_NAMESPACES = {
  layout: ['layout'],
  treatments: ['treatments'],
  doctors: ['doctors'],
  doctordetail: ['doctordetail'],
  providers: ['providers'],
  admin_extended: ['layout'], // Additional admin namespaces
  partner_extended: ['navigation'], // Additional partner namespaces
} as const;

// Route-specific namespaces that should only be loaded when needed
// Reduced initial load by making more namespaces lazy-loaded
const ROUTE_NAMESPACES: Record<string, string[]> = {
  '/': ['home'],
  '/treatments': ['treatments_page'], // Removed 'treatments' to load on-demand
  '/doctors': [], // Load 'doctors' namespace lazily
  '/doctor': ['provider_detail'], // Removed 'doctordetail' to load on-demand
  '/providers': [], // Load 'providers' namespace lazily
  '/provider': ['provider_detail'], // Keep core provider_detail
  '/admin': ['admin'], // Reduced to only load admin core initially
  '/partner-dashboard': ['partnerdashboard'], // Reduced to only load partner core initially
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const flattenBundle = (bundle: unknown): Record<string, unknown> => {
  if (!bundle || typeof bundle !== 'object') {
    return {};
  }

  const flattened: Record<string, unknown> = {};

  const walk = (node: unknown, prefix = ''): void => {
    if (!isPlainObject(node)) {
      if (prefix && typeof prefix === 'string') {
        flattened[prefix] = node;
      }
      return;
    }

    const entries = Object.entries(node);
    for (const [key, value] of entries) {
      if (typeof key !== 'string') continue;
      const nextPrefix = prefix ? `${prefix}.${key}` : key;

      if (isPlainObject(value)) {
        walk(value, nextPrefix);
      } else if (typeof nextPrefix === 'string') {
        flattened[nextPrefix] = value;
      }
    }
  };

  walk(bundle);
  return flattened;
};

// Default context value to prevent errors during initialization
const defaultContextValue: TranslationContextValue = {
  t: (
    page: string,
    context: string,
    fallback?: string | null,
    _variables?: Record<string, unknown>
  ) => fallback ?? context,
  currentLanguage: DEFAULT_LANG,
  changeLanguage: () => Promise.resolve(),
  loading: true,
  forceUpdate: 0,
  translations: {},
  fallbackTranslations: {},
  debugTranslations: () => {},
};

// Translation Context with default value
const TranslationContext = createContext<TranslationContextValue>(defaultContextValue);

// Translation Provider Component
export function TranslationProvider({ children, initialLanguage }: TranslationProviderProps) {
  const params = useParams();
  const location = useLocation();

  // Prioritize initialLanguage prop (from parent LanguageWrapper) over URL params
  const urlLang = params.lang;
  const normalizedUrlLang = urlLang ? normalizeLanguageCode(urlLang) : 'en';
  const effectiveLanguage = initialLanguage ?? normalizedUrlLang;

  const [currentLanguage, setCurrentLanguage] = useState(effectiveLanguage);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const queryClient = useQueryClient();

  // Determine which namespaces to load based on current route
  const getRequiredNamespaces = (pathname: string): string[] => {
    const requiredNamespaces = [...CORE_NAMESPACES]; // Always load core namespaces

    // Find route-specific namespaces
    for (const [routePattern, namespaces] of Object.entries(ROUTE_NAMESPACES)) {
      if (pathname.startsWith(routePattern) || pathname.includes(routePattern)) {
        requiredNamespaces.push(...namespaces);
      }
    }

    // Remove duplicates
    return [...new Set(requiredNamespaces)];
  };

  const preferredLanguage = normalizeLanguageCode(currentLanguage || effectiveLanguage || 'en');
  const requiredNamespaces = getRequiredNamespaces(location.pathname);

  // Use React Query for translations
  const fallbackQuery = useQuery({
    queryKey: ['translations', FALLBACK_LANGUAGE, requiredNamespaces.join(','), 'v3'], // Force cache invalidation
    queryFn: async (): Promise<Record<string, unknown>> => {
      const bundle = await loadLanguageTranslations(FALLBACK_LANGUAGE, {
        forceRefresh: false,
        namespaces: requiredNamespaces,
      });
      return flattenBundle(bundle);
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  const preferredQuery = useQuery({
    queryKey: ['translations', preferredLanguage, requiredNamespaces.join(','), 'v3'], // Force cache invalidation
    queryFn: async (): Promise<Record<string, unknown> | null> => {
      if (preferredLanguage === FALLBACK_LANGUAGE) {
        return null;
      }
      const bundle = await loadLanguageTranslations(preferredLanguage, {
        forceRefresh: false,
        namespaces: requiredNamespaces,
      });
      const flattened = flattenBundle(bundle);
      return flattened;
    },
    enabled: preferredLanguage !== FALLBACK_LANGUAGE,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  const currentTranslations = useMemo(
    () =>
      preferredLanguage === FALLBACK_LANGUAGE
        ? (fallbackQuery.data ?? {})
        : (preferredQuery.data ?? fallbackQuery.data ?? {}),
    [preferredLanguage, fallbackQuery.data, preferredQuery.data]
  );
  const fallbackTranslations = useMemo(() => fallbackQuery.data ?? {}, [fallbackQuery.data]);
  const loading =
    fallbackQuery.isLoading ||
    (preferredLanguage !== FALLBACK_LANGUAGE && preferredQuery.isLoading);

  // Helper function to schedule background task with idle callback or timeout fallback
  const scheduleBackgroundTask = useCallback((task: () => void, delay: number): void => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(task, { timeout: delay });
    } else {
      setTimeout(task, delay);
    }
  }, []);

  // Helper function to preload a single language
  const preloadLanguage = useCallback(
    (lang: string): void => {
      const preloadFn = () => {
        void loadLanguageTranslations(lang, { forceRefresh: false });
      };
      scheduleBackgroundTask(preloadFn, 5000);
    },
    [scheduleBackgroundTask]
  );

  // Helper function to preload namespaces for current language
  const preloadNamespacesForLanguage = useCallback((): void => {
    if (preferredLanguage === FALLBACK_LANGUAGE) return;

    const preloadNamespaceFn = () => {
      void preloadNamespaces(preferredLanguage, CORE_NAMESPACES);
    };
    scheduleBackgroundTask(preloadNamespaceFn, 3000);
  }, [preferredLanguage, scheduleBackgroundTask]);

  // Helper function to preload all critical languages
  const preloadCriticalLanguages = useCallback((): void => {
    // Only preload if we're not in development and have network connectivity
    if (!import.meta.env.PROD || !navigator.onLine) return;

    try {
      // Preload languages in background with lower priority
      for (const lang of CRITICAL_LANGUAGES) {
        if (lang !== FALLBACK_LANGUAGE && lang !== preferredLanguage) {
          preloadLanguage(lang);
        }
      }

      preloadNamespacesForLanguage();
    } catch {
      // Silent fail - preloading is not critical
    }
  }, [preferredLanguage, preloadLanguage, preloadNamespacesForLanguage]);

  // Preload critical languages and namespaces in background for faster switching
  useEffect(() => {
    // Start preloading after initial translations are loaded
    if (!loading && fallbackQuery.data) {
      preloadCriticalLanguages();
    }
  }, [loading, fallbackQuery.data, preferredLanguage, preloadCriticalLanguages]);

  // Listen for translation updates from admin panel
  useEffect(() => {
    const handleTranslationsUpdated = () => {
      void queryClient.invalidateQueries({ queryKey: ['translations'] });
    };

    window.addEventListener('translationsUpdated', handleTranslationsUpdated);
    return () => window.removeEventListener('translationsUpdated', handleTranslationsUpdated);
  }, [queryClient]);

  // Sync language with initialLanguage prop (which comes from URL via LanguageWrapper)
  // Skip syncing if we're currently in the middle of a programmatic language change
  useEffect(() => {
    if (isChangingLanguage) return;

    if (initialLanguage && initialLanguage !== currentLanguage) {
      // Clean up old language translations before switching
      cleanupUnusedLanguages(initialLanguage);

      setCurrentLanguage(initialLanguage);
      localStorage.setItem('preferred_language', initialLanguage);
      // Force re-render to update all components with new language
      setForceUpdate(prev => prev + 1);
    }
  }, [initialLanguage, currentLanguage, isChangingLanguage]);

  // Set document direction and RTL class for proper text direction
  useEffect(() => {
    // Only run on client side to prevent SSR/hydration mismatches
    if (typeof window === 'undefined') return;

    try {
      const rtlLanguages = ['ar']; // Add other RTL languages if needed
      const isRTL = rtlLanguages.includes(currentLanguage);
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.documentElement.classList.toggle('rtl', isRTL);
    } catch (error) {
      // Silently fail DOM manipulation errors to prevent crashes
      console.warn('Failed to set document direction:', error);
    }
  }, [currentLanguage]);

  // Helper function to handle fallback replacement for placeholders
  const handleFallbackPlaceholders = useCallback((text: string): string => {
    const hasPlaceholders = /{{[^}]+}}/.test(text);
    if (hasPlaceholders) {
      // Replace {{count}} with 0 as a fallback
      return text.replaceAll('{{count}}', '0').replaceAll(/{{[^}]+}}/g, '');
    }
    return text;
  }, []);

  // Helper function to safely convert value to string
  const valueToString = useCallback((value: unknown): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) return '[object]';
    return typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
  }, []);

  // Helper function to replace a single variable pattern
  const replaceSingleVariable = useCallback(
    (text: string, key: string, value: unknown): string => {
      const safeValue = valueToString(value);
      const doubleBracePattern = `{{${key}}}`;
      const dollarBracePattern = `\${${key}}`;
      return text
        .replaceAll(doubleBracePattern, safeValue)
        .replaceAll(dollarBracePattern, safeValue);
    },
    [valueToString]
  );

  // Helper function to replace variables in text
  const replaceTemplateVariables = useCallback(
    (text: string, variables: Record<string, unknown>): string => {
      let result = text;
      // Safely iterate over variables object
      for (const [key, value] of Object.entries(variables)) {
        result = replaceSingleVariable(result, key, value);
      }
      return result;
    },
    [replaceSingleVariable]
  );

  // Helper function to convert unknown value to safe string
  const safeStringify = useCallback((value: unknown): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) return '[object]';
    return typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';
  }, []);

  // Helper function to check if text has unreplaced placeholders
  const hasUnreplacedPlaceholders = useCallback((text: string): boolean => {
    return /{{[^}]+}}/.test(text);
  }, []);

  // Helper function to replace template variables like {{count}} with actual values
  const replaceVariables = useCallback(
    (text: unknown, variables?: Record<string, unknown>): string => {
      if (!text || typeof text !== 'string') {
        return safeStringify(text);
      }

      // If no variables provided, handle fallback placeholders
      if (!variables || typeof variables !== 'object') {
        return handleFallbackPlaceholders(text);
      }

      const result = replaceTemplateVariables(text, variables);

      // Final safety check: if there are still unreplaced placeholders after variable replacement
      if (hasUnreplacedPlaceholders(result)) {
        return handleFallbackPlaceholders(result);
      }

      return result;
    },
    [handleFallbackPlaceholders, hasUnreplacedPlaceholders, replaceTemplateVariables, safeStringify]
  );

  // Track missing translation keys to avoid duplicate warnings
  const missingKeysRef = useRef(new Set());

  // Helper function to build new URL with language prefix
  const buildLanguageUrl = useCallback(
    (nextLanguage: string): string => {
      const urlCode = languageToUrlCode(nextLanguage);
      const pathParts = location.pathname.split('/').filter(Boolean);

      // Remove old language prefix if exists
      if (pathParts.length > 0 && params.lang) {
        pathParts.shift();
      }

      // Build new path with language prefix
      const newPath = `/${urlCode}${pathParts.length > 0 ? '/' + pathParts.join('/') : ''}`;

      // Preserve search parameters and hash
      return `${newPath}${location.search}${location.hash}`;
    },
    [location.pathname, location.search, location.hash, params.lang]
  );

  // Helper function to update language state
  const updateLanguageState = useCallback((nextLanguage: string): void => {
    setCurrentLanguage(nextLanguage);
    localStorage.setItem('preferred_language', nextLanguage);
    // Mark that user has explicitly chosen a language (overrides domain default)
    localStorage.setItem('language_user_override', 'true');
    // Force re-render of all components immediately
    setForceUpdate(prev => prev + 1);
  }, []);

  // Helper function to trigger language change events
  const triggerLanguageChangeEvents = useCallback(
    (nextLanguage: string): void => {
      setTimeout(() => {
        // Invalidate and refetch translations after navigation
        void queryClient.invalidateQueries({ queryKey: ['translations', nextLanguage] });

        window.dispatchEvent(
          new CustomEvent('languageChanged', {
            detail: {
              language: nextLanguage,
              timestamp: Date.now(),
              translationsCount: 0, // Will be updated after loading
            },
          })
        );
        // Clear the flag after the change is complete
        setIsChangingLanguage(false);
      }, 100);
    },
    [queryClient]
  );

  // Helper function to process fallback text
  const processFallbackText = useCallback((fallback: string | null, context: string): string => {
    const fallbackSource = fallback ?? context;
    const fallbackText =
      typeof fallbackSource === 'string' && fallbackSource.includes('_')
        ? fallbackSource
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : fallbackSource;
    return String(fallbackText);
  }, []);

  // Helper function to handle missing translation
  const handleMissingTranslation = useCallback(
    (
      page: string,
      fallback: string | null,
      context: string,
      variables: Record<string, unknown>
    ): string => {
      // Debug logging for missing translations in development (only log once per key)
      const key = `${page}.${context}`;
      if (import.meta.env.MODE === 'development' && !loading && !missingKeysRef.current.has(key)) {
        missingKeysRef.current.add(key);
        // Missing translation - using fallback
      }

      const fallbackText = processFallbackText(fallback, context);
      return replaceVariables(fallbackText, variables);
    },
    [loading, processFallbackText, replaceVariables]
  );

  // Helper function to safely convert translation to string
  const safeTranslationToString = useCallback((value: unknown): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) return '[object]';
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '';
  }, []);

  // Helper function to process found translation
  const processTranslation = useCallback(
    (
      translation: unknown,
      fallback: string | null,
      context: string,
      variables: Record<string, unknown>
    ): string => {
      const translatedText = translation ?? fallback ?? context;

      const safeTranslatedText = safeTranslationToString(translatedText);
      const result = replaceVariables(safeTranslatedText, variables);

      // Development-only warning if placeholders still exist
      if (import.meta.env.MODE === 'development' && /{{[^}]+}}/.test(result)) {
        // Unreplaced placeholders in translation (development only)
      }

      return result;
    },
    [replaceVariables, safeTranslationToString]
  );

  // Enhanced translation function with template variable support
  const t = useCallback(
    (
      page: string,
      context: string,
      fallback: string | null = null,
      variables: Record<string, unknown> = {}
    ): string => {
      // Validate input to prevent injection
      if (!page || !context || typeof page !== 'string' || typeof context !== 'string') {
        return processFallbackText(fallback, context);
      }

      const key = `${page}.${context}`;

      const translation: unknown =
        key in currentTranslations
          ? currentTranslations[key]
          : key in fallbackTranslations
            ? fallbackTranslations[key]
            : undefined;

      if (!translation) {
        return handleMissingTranslation(page, fallback, context, variables);
      }

      return processTranslation(translation, fallback, context, variables);
    },
    [
      currentTranslations,
      fallbackTranslations,
      handleMissingTranslation,
      processTranslation,
      processFallbackText,
    ]
  );

  // Enhanced change language function - updates URL and state
  const changeLanguage = useCallback(
    (languageCode: string): Promise<void> => {
      const nextLanguage = normalizeLanguageCode(languageCode);

      // Prevent sync useEffect from interfering during programmatic language change
      setIsChangingLanguage(true);

      // Clear translation cache for clean loading
      queryClient.removeQueries({ queryKey: ['translations'] });

      // Clean up unused language translations to free memory
      cleanupUnusedLanguages(nextLanguage);

      // Update URL with new language code first
      const fullPath = buildLanguageUrl(nextLanguage);

      // Force full page reload to ensure all dynamic translations update properly
      window.location.href = fullPath;

      // Now update state after URL has been updated
      updateLanguageState(nextLanguage);

      // Trigger global events and clear cache invalidation
      triggerLanguageChangeEvents(nextLanguage);

      // Return a resolved promise since the page will reload
      return Promise.resolve();
    },
    [queryClient, buildLanguageUrl, updateLanguageState, triggerLanguageChangeEvents]
  );

  // Debug function to inspect translations
  const debugTranslations = useCallback((): void => {
    // Debug function for development - no console output in production
  }, []);

  // Expose debug function globally for testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.debugTranslations = debugTranslations;
    }
  }, [debugTranslations]);

  // Memoize context value to prevent unnecessary re-renders of all consumers
  const value = useMemo(
    () => ({
      t,
      currentLanguage,
      changeLanguage,
      loading,
      forceUpdate,
      translations: currentTranslations,
      fallbackTranslations,
      debugTranslations,
    }),
    [
      t,
      currentLanguage,
      changeLanguage,
      loading,
      forceUpdate,
      currentTranslations,
      fallbackTranslations,
      debugTranslations,
    ]
  );

  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
}

// Export context for the hook to use
export { TranslationContext };
